import { Config_WarrantyContentSource, Config_WarrantyMergeStrategy } from '@rabbit/config/enums';

export type WarrantyInfoItem = { title: string; description?: string; included?: string };
export type WarrantyInfoSection = { title: string; type: string; items?: WarrantyInfoItem[]; children?: any };

export type TemplateContentFetcher = () => Promise<{ info: WarrantyInfoSection[]; terms: WarrantyInfoSection[] }>; 

export async function resolveWarrantyContent(options: {
  source: Config_WarrantyContentSource;
  mergeStrategy: Config_WarrantyMergeStrategy;
  vendableInfoOverride?: WarrantyInfoSection[];
  vendableTermsOverride?: WarrantyInfoSection[];
  fetchTemplateContent: TemplateContentFetcher;
  tenantInfoDefault?: WarrantyInfoSection[]; // Optional for callers that pre-fetched defaults (unused in current resolver but kept for future)
  tenantTermsDefault?: WarrantyInfoSection[];
}): Promise<{ info: WarrantyInfoSection[]; terms: WarrantyInfoSection[] }> {
  const vendableInfo = options.vendableInfoOverride ?? [];
  const vendableTerms = options.vendableTermsOverride ?? [];

  if (options.source === Config_WarrantyContentSource.VENDABLE) {
    return { info: vendableInfo, terms: vendableTerms };
  }

  const templateContent = await options.fetchTemplateContent();

  if (options.source === Config_WarrantyContentSource.TEMPLATE) {
    return { info: templateContent.info ?? [], terms: templateContent.terms ?? [] };
  }

  const mergeSections = (
    base: WarrantyInfoSection[] | undefined,
    extra: WarrantyInfoSection[] | undefined
  ): WarrantyInfoSection[] => {
    const baseArr = base ?? [];
    const extraArr = extra ?? [];

    if (options.mergeStrategy === Config_WarrantyMergeStrategy.PREPEND) {
      return [...extraArr, ...baseArr];
    }
    if (options.mergeStrategy === Config_WarrantyMergeStrategy.APPEND) {
      return [...baseArr, ...extraArr];
    }

    // override-duplicates: by title (case-insensitive), merge items
    const byTitle = new Map<string, WarrantyInfoSection>();
    baseArr.forEach((s) => byTitle.set((s.title || '').toLowerCase(), s));
    extraArr.forEach((s) => {
      const key = (s.title || '').toLowerCase();
      if (!byTitle.has(key)) {
        byTitle.set(key, s);
      } else {
        const existing = byTitle.get(key)!;
        const mergedItems = [
          ...((existing.items as any[]) || []),
          ...((s.items as any[]) || []),
        ];
        byTitle.set(key, { ...existing, items: mergedItems });
      }
    });
    return Array.from(byTitle.values());
  };

  return {
    info: mergeSections(templateContent.info, vendableInfo),
    terms: mergeSections(templateContent.terms, vendableTerms),
  };
}


